import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { bookingsAPI, paymentsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { formatPrice, formatDate, getStatusColor, getPaymentStatusColor } from '../lib/utils';
import { Calendar, MapPin, Users, Loader2, CreditCard, XCircle } from 'lucide-react';

export default function BookingsPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const loadBookings = async () => {
      try {
        const response = await bookingsAPI.getAll();
        setBookings(response.data);
      } catch (error) {
        console.error('Error loading bookings:', error);
        toast.error('Failed to load bookings');
      } finally {
        setLoading(false);
      }
    };
    loadBookings();
  }, [isAuthenticated, navigate]);

  const handleCancel = async (bookingId) => {
    setCancellingId(bookingId);
    try {
      await bookingsAPI.cancel(bookingId);
      toast.success('Booking cancelled successfully');
      // Refresh bookings
      const response = await bookingsAPI.getAll();
      setBookings(response.data);
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setCancellingId(null);
    }
  };

  const handlePayment = async (bookingId) => {
    navigate(`/checkout/${bookingId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <Navbar />
      
      {/* Header */}
      <section className="bg-primary py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
            My Bookings
          </h1>
          <p className="text-white/80 mt-2">
            View and manage your tour reservations
          </p>
        </div>
      </section>

      {/* Bookings List */}
      <section className="py-12" data-testid="bookings-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {bookings.length === 0 ? (
            <Card className="p-12 text-center rounded-2xl" data-testid="no-bookings">
              <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="font-heading text-xl font-semibold mb-2">No Bookings Yet</h2>
              <p className="text-muted-foreground mb-6">
                You haven't made any reservations yet. Start exploring Puerto Rico!
              </p>
              <Button 
                onClick={() => navigate('/tours')} 
                className="rounded-full"
                data-testid="explore-tours-btn"
              >
                Explore Tours
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <Card 
                  key={booking.id} 
                  className="p-6 rounded-2xl"
                  data-testid={`booking-card-${booking.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-heading text-lg font-semibold">
                          {booking.tour_title}
                        </h3>
                        <Badge className={getStatusColor(booking.status)}>
                          {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                        </Badge>
                        <Badge className={getPaymentStatusColor(booking.payment_status)}>
                          {booking.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                        </Badge>
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDate(booking.date)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span>at {booking.time}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          <span>{booking.guests} guests</span>
                        </div>
                      </div>

                      {booking.special_requests && (
                        <p className="text-sm text-muted-foreground mt-2">
                          Note: {booking.special_requests}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <span className="font-heading text-xl font-bold text-primary">
                        {formatPrice(booking.total_price)}
                      </span>
                      
                      <div className="flex gap-2">
                        {booking.payment_status === 'pending' && booking.status !== 'cancelled' && (
                          <Button 
                            size="sm"
                            className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full"
                            onClick={() => handlePayment(booking.id)}
                            data-testid={`pay-btn-${booking.id}`}
                          >
                            <CreditCard className="h-4 w-4 mr-1" />
                            Pay Now
                          </Button>
                        )}
                        
                        {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="rounded-full"
                            onClick={() => handleCancel(booking.id)}
                            disabled={cancellingId === booking.id}
                            data-testid={`cancel-btn-${booking.id}`}
                          >
                            {cancellingId === booking.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <XCircle className="h-4 w-4 mr-1" />
                                Cancel
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
